import { z } from 'zod';
import { isoTimestamp } from './common';
import { attachmentSchema } from './files';
import { windowStep } from '../history-window';

/**
 * Session context management. An agent defines session levels, broad → specific (for example
 * `global → store → user`). Each level is keyed by nothing (one session shared by everyone who uses
 * the agent), a request variable (endpoint mapping value or playground request context, e.g.
 * `store_id`), the client's thread id, or the caller (API key / console user).
 *
 * - Every level can keep **notes**: shared context the agent sees in its system prompt and can
 *   update with the `update_session_memory` tool (e.g. a store's brand colours, a user's name).
 * - The **conversation** lives at the most specific level the request identifies that keeps
 *   history, within that level's context budget. One user's messages never reach another user.
 * - Levels whose key is missing from a request are skipped.
 */

export const SESSION_LEVEL_NAME = /^[a-z][a-z0-9_]{0,31}$/;
const identifier = z
  .string()
  .regex(/^[A-Za-z_][A-Za-z0-9_]{0,63}$/, 'Use letters, digits and _ (a variable name)');

export const sessionKeySchema = z.discriminatedUnion('source', [
  /** One session for the whole agent. */
  z.object({ source: z.literal('none') }),
  /** A request variable: an endpoint mapping value or a playground request context value. */
  z.object({ source: z.literal('variable'), variable: identifier }),
  /** The thread id the client sends. */
  z.object({ source: z.literal('thread') }),
  /** The API key (gateway) or console user (playground). */
  z.object({ source: z.literal('caller') }),
]);
export type SessionKey = z.infer<typeof sessionKeySchema>;

export const sessionLevelSchema = z
  .object({
    name: z
      .string()
      .regex(SESSION_LEVEL_NAME, 'Use 1–32 lowercase letters, digits or _, starting with a letter'),
    description: z.string().trim().max(300).default(''),
    key: sessionKeySchema,
    history: z
      .object({
        enabled: z.boolean().default(false),
        /** Most recent messages sent to the model each turn. */
        maxMessages: z.number().int().min(1).max(200).default(20),
        /** Approximate token budget of that history (oldest messages are dropped first). */
        maxTokens: z.number().int().min(100).max(200_000).default(8_000),
      })
      .strict()
      .default({ enabled: false, maxMessages: 20, maxTokens: 8_000 }),
    notes: z
      .object({
        enabled: z.boolean().default(true),
        maxChars: z.number().int().min(100).max(20_000).default(4_000),
        /** The agent may update them with `update_session_memory`. */
        writable: z.boolean().default(true),
      })
      .strict()
      .default({ enabled: true, maxChars: 4_000, writable: true }),
  })
  .strict();
export type SessionLevel = z.infer<typeof sessionLevelSchema>;

export const MAX_SESSION_LEVELS = 5;

export const sessionConfigSchema = z
  .object({ levels: z.array(sessionLevelSchema).max(MAX_SESSION_LEVELS).default([]) })
  .strict()
  .superRefine((config, ctx) => {
    const seen = new Set<string>();
    config.levels.forEach((level, i) => {
      if (seen.has(level.name))
        ctx.addIssue({
          code: 'custom',
          path: ['levels', i, 'name'],
          message: 'Names must be unique',
        });
      seen.add(level.name);
    });
    const shared = config.levels.filter((l) => l.key.source === 'none').length;
    if (shared > 1)
      ctx.addIssue({
        code: 'custom',
        path: ['levels'],
        message: 'Only one level can be shared by everyone (no key)',
      });
  });
export type SessionConfig = z.infer<typeof sessionConfigSchema>;
export const DEFAULT_SESSION_CONFIG: SessionConfig = { levels: [] };

/** A starting point like the user's example: global, store and user levels. */
export const EXAMPLE_SESSION_LEVELS: SessionLevel[] = [
  {
    name: 'global',
    description: 'Shared by everyone who uses the agent',
    key: { source: 'none' },
    history: { enabled: false, maxMessages: 20, maxTokens: 8_000 },
    notes: { enabled: true, maxChars: 4_000, writable: true },
  },
  {
    name: 'store',
    description: 'One per store',
    key: { source: 'variable', variable: 'store_id' },
    history: { enabled: false, maxMessages: 20, maxTokens: 8_000 },
    notes: { enabled: true, maxChars: 4_000, writable: true },
  },
  {
    name: 'user',
    description: 'One per user: their conversation',
    key: { source: 'variable', variable: 'user_id' },
    history: { enabled: true, maxMessages: 30, maxTokens: 12_000 },
    notes: { enabled: true, maxChars: 2_000, writable: true },
  },
];

/** The request variables the levels are keyed by (e.g. `store_id`, `user_id`). */
export function sessionVariables(levels: SessionLevel[]): string[] {
  return [...new Set(levels.flatMap((l) => (l.key.source === 'variable' ? [l.key.variable] : [])))];
}

export const MAX_SESSION_KEY_LENGTH = 200;

/** What identifies the request, for resolving level keys. */
export interface SessionRequestKeys {
  variables: Record<string, unknown>;
  threadId?: string | null;
  callerKey: string;
}

export interface ResolvedSessionLevel {
  level: SessionLevel;
  key: string;
}

/** The levels a request identifies, in order; levels without a key are skipped. */
export function resolveSessionLevels(
  config: SessionConfig,
  request: SessionRequestKeys,
): ResolvedSessionLevel[] {
  const out: ResolvedSessionLevel[] = [];
  for (const level of config.levels) {
    let key: string | null = null;
    switch (level.key.source) {
      case 'none':
        key = '*';
        break;
      case 'thread':
        key = request.threadId?.trim() || null;
        break;
      case 'caller':
        key = request.callerKey;
        break;
      case 'variable': {
        const raw = request.variables[level.key.variable];
        key =
          typeof raw === 'string' || typeof raw === 'number' || typeof raw === 'boolean'
            ? String(raw).trim() || null
            : null;
      }
    }
    if (key && key.length <= MAX_SESSION_KEY_LENGTH) out.push({ level, key });
  }
  return out;
}

/** The level that holds the conversation: the most specific resolved level that keeps history. */
export function conversationLevel(levels: ResolvedSessionLevel[]): ResolvedSessionLevel | null {
  for (let i = levels.length - 1; i >= 0; i--)
    if (levels[i]!.level.history.enabled) return levels[i]!;
  return null;
}

/** Rough token count (≈ 4 characters per token; an attachment counts as 1,000). */
export function approxTokens(text: string, attachments = 0): number {
  return Math.ceil(text.length / 4) + attachments * 1_000;
}

/**
 * The history window within the budget, oldest first: at most the last `maxMessages`, then the
 * oldest are dropped, a whole `windowStep` at a time, while the tokens exceed `maxTokens`. The history never starts with an
 * assistant message (the model would see an answer without its question). Callers pass the aligned
 * window (see `alignedWindowStart`) so the history stays the same across turns.
 */
export function fitHistory<T extends { role: string; approxTokens: number }>(
  oldestFirst: T[],
  budget: { maxMessages: number; maxTokens: number },
): T[] {
  const kept = oldestFirst.slice(-budget.maxMessages);
  let tokens = kept.reduce((sum, m) => sum + m.approxTokens, 0);
  // Drop in whole steps (never past the newest) so the start moves rarely and the cache holds.
  const step = windowStep(budget.maxMessages);
  while (kept.length > 1 && tokens > budget.maxTokens)
    for (const m of kept.splice(0, Math.min(step, kept.length - 1))) tokens -= m.approxTokens;
  if (kept.length === 1 && tokens > budget.maxTokens) kept.length = 0;
  while (kept[0]?.role === 'assistant') kept.shift();
  return kept;
}

// --- API ---

export const sessionDtoSchema = z.object({
  id: z.uuid(),
  agentId: z.uuid(),
  agentName: z.string(),
  level: z.string(),
  key: z.string(),
  notes: z.string(),
  messageCount: z.number().int(),
  approxTokens: z.number().int(),
  lastActiveAt: isoTimestamp,
  createdAt: isoTimestamp,
});
export type SessionDto = z.infer<typeof sessionDtoSchema>;

export const sessionMessageDtoSchema = z.object({
  id: z.uuid(),
  role: z.enum(['user', 'assistant']),
  content: z.string(),
  attachments: z.array(attachmentSchema),
  runId: z.uuid().nullable(),
  approxTokens: z.number().int(),
  createdAt: isoTimestamp,
});
export type SessionMessageDto = z.infer<typeof sessionMessageDtoSchema>;

export const sessionDetailDtoSchema = sessionDtoSchema.extend({
  messages: z.array(sessionMessageDtoSchema),
});
export type SessionDetailDto = z.infer<typeof sessionDetailDtoSchema>;

export const sessionListQuerySchema = z.object({
  cursor: z.uuid().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  agentId: z.uuid().optional(),
  level: z.string().regex(SESSION_LEVEL_NAME).optional(),
  /** Matches the key (substring, case-insensitive). */
  q: z.string().trim().max(200).optional(),
});
export type SessionListQuery = z.infer<typeof sessionListQuerySchema>;

export const updateSessionRequestSchema = z.object({ notes: z.string().max(20_000) });
export type UpdateSessionRequest = z.infer<typeof updateSessionRequestSchema>;
